import React from "react";
import { Link } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const Footer = ({ currentTheme }) => {
  const currentYear = new Date()?.getFullYear();

  const navigationLinks = [
    { name: "Home", path: "/adaptive-homepage-hub" },
    { name: "Portfolio", path: "/dynamic-portfolio-ecosystem" },
    { name: "Services", path: "/services-architecture-hub" },
    { name: "Case Studies", path: "/case-studies-showcase" },
    { name: "Contact", path: "/contact-scheduling-hub" },
  ];

  const serviceLinks = [
    { name: "Event & Wedding Videography", path: "/services-architecture-hub" },
    {
      name: "Real Estate Visual Marketing",
      path: "/services-architecture-hub",
    },
    { name: "Brand & Marketing Content", path: "/services-architecture-hub" },
    { name: "Design Fiction & Conceptual", path: "/services-architecture-hub" },
  ];

  const socialLinks = [
    {
      name: "Instagram",
      icon: "Instagram",
      url: "https://instagram.com/skycreststudios",
      followers: "12K",
    },
    {
      name: "YouTube",
      icon: "Youtube",
      url: "https://youtube.com/skycreststudios",
      followers: "8K",
    },
    {
      name: "Vimeo",
      icon: "Video",
      url: "https://vimeo.com/skycreststudios",
      followers: "5K",
    },
    {
      name: "LinkedIn",
      icon: "Linkedin",
      url: "https://linkedin.com/company/skycreststudios",
      followers: "3K",
    },
  ];

  const getThemeAccent = () => {
    const accents = {
      events: "text-rose-500",
      realestate: "text-blue-600",
      marketing: "text-orange-600",
      design: "text-purple-600",
      default: "text-accent",
    };
    return accents?.[currentTheme] || accents?.default;
  };

  const getThemeBg = () => {
    const backgrounds = {
      events: "from-rose-50/30 to-amber-50/30",
      realestate: "from-blue-50/30 to-cyan-50/30",
      marketing: "from-orange-50/30 to-red-50/30",
      design: "from-purple-50/30 to-pink-50/30",
      default: "from-muted/20 to-background",
    };
    return backgrounds?.[currentTheme] || backgrounds?.default;
  };

  return (
    <footer className={`relative overflow-hidden transition-all duration-1000`}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 2px 2px, #000000 1px, transparent 0)",
            backgroundSize: "60px 60px",
          }}
        ></div>
      </div>

      {/* Enhanced Main Footer Content */}
      <div className="bg-background border-b border-border/50">
        <div className="container mx-auto px-6 py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Enhanced Brand Column */}
            <div className="lg:col-span-1 space-y-8">
              <Link
                to="/adaptive-homepage-hub"
                className="group flex items-center space-x-3 mb-8"
              >
                <span className="text-2xl font-brand tracking-wide transition-colors duration-300 text-primary">
                  <span>Skycrest</span>
                  <span className="text-accent">Films</span>
                </span>
              </Link>

              <p className="text-muted-foreground leading-relaxed">
                Visual storytelling mastery across diverse industries. We don't
                just create videos; we craft visual experiences that elevate
                your brand and drive measurable results.
              </p>

              {/* Awards & Recognition */}
              <div className="space-y-3">
                <div className="text-sm font-medium text-foreground">
                  Awards & Recognition
                </div>
                <div className="flex flex-wrap gap-2">
                  <div className="flex items-center space-x-2 px-3 py-1 bg-accent/10 rounded-full">
                    <Icon name="Award" size={14} className="text-accent" />
                    <span className="text-xs text-accent font-medium">
                      Vimeo Staff Pick
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 px-3 py-1 bg-accent/10 rounded-full">
                    <Icon name="Star" size={14} className="text-accent" />
                    <span className="text-xs text-accent font-medium">
                      5-Star Google Reviews
                    </span>
                  </div>
                </div>
              </div>

              {/* Enhanced Social Links */}
              <div className="space-y-4">
                <div className="text-sm font-medium text-foreground">
                  Follow Our Journey
                </div>
                <div className="flex space-x-3">
                  {socialLinks?.map((social) => (
                    <a
                      key={social?.name}
                      href={social?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`group relative w-12 h-12 bg-muted hover:bg-accent hover:text-accent-foreground rounded-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg ${getThemeAccent()}`}
                      aria-label={`Follow us on ${social?.name}`}
                    >
                      <Icon name={social?.icon} size={20} />
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-muted text-foreground text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                        {social?.followers} followers
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Navigation Column */}
            <div>
              <h4 className="text-foreground font-semibold mb-8 text-lg">
                Quick Links
              </h4>
              <ul className="space-y-4">
                {navigationLinks?.map((link) => (
                  <li key={link?.path}>
                    <Link
                      to={link?.path}
                      className="group flex items-center space-x-2 text-muted-foreground hover:text-accent transition-colors duration-300"
                    >
                      <Icon
                        name="ArrowRight"
                        size={14}
                        className="opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all duration-300"
                      />
                      <span>{link?.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services Column */}
            <div>
              <h4 className="text-foreground font-semibold mb-8 text-lg">
                Our Services
              </h4>
              <ul className="space-y-4">
                {serviceLinks?.map((service, index) => (
                  <li key={index}>
                    <Link
                      to={service?.path}
                      className="group flex items-center space-x-2 text-muted-foreground hover:text-accent transition-colors duration-300"
                    >
                      <Icon
                        name="ArrowRight"
                        size={14}
                        className="opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all duration-300"
                      />
                      <span>{service?.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Enhanced Contact Column */}
            <div>
              <h4 className="text-foreground font-semibold mb-8 text-lg">
                Get in Touch
              </h4>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <Icon
                    name="MapPin"
                    size={20}
                    className={`mt-1 ${getThemeAccent()}`}
                  />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">
                      Rockport, MA 01966
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <Icon name="Phone" size={20} className={getThemeAccent()} />
                  <div>
                    <a
                      href="tel:+15089805187"
                      className="text-sm text-muted-foreground hover:text-accent transition-colors font-medium"
                    >
                      (508) 980-5187
                    </a>
                    <p className="text-xs text-muted-foreground">
                      Same day response
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <Icon name="Mail" size={20} className={getThemeAccent()} />
                  <div>
                    <a
                      href="mailto:hello@skycreststudios.com"
                      className="text-sm text-muted-foreground hover:text-accent transition-colors font-medium"
                    >
                      hello@skycreststudios.com
                    </a>
                    <p className="text-xs text-muted-foreground">
                      Business inquiries
                    </p>
                  </div>
                </div>

                <div className="pt-4">
                  <Button
                    variant="default"
                    size="sm"
                    asChild
                    className="w-full bg-cta hover:bg-cta/90 text-cta-foreground shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                    iconName="Calendar"
                    iconPosition="left"
                  >
                    <a
                      href="#book-consultation"
                      onClick={(e) => {
                        e.preventDefault();
                        if (!document.getElementById("MeetingsEmbedCode-js")) {
                          const s = document.createElement("script");
                          s.src =
                            "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
                          s.async = true;
                          s.defer = true;
                          s.id = "MeetingsEmbedCode-js";
                          document.body.appendChild(s);
                        }
                        let overlay = document.getElementById(
                          "hs-meetings-overlay"
                        );
                        if (!overlay) {
                          overlay = document.createElement("div");
                          overlay.id = "hs-meetings-overlay";
                          overlay.className =
                            "fixed inset-0 z-[60] flex justify-center items-start p-0 bg-[#425B76] md:bg-black/50";
                          overlay.style.overflow = "hidden";
                          overlay.style.overscrollBehavior = "contain";
                          const inner = document.createElement("div");
                          inner.className =
                            "relative w-screen h-screen md:w-full md:max-w-4xl md:h-[90vh] bg-[#425B76] md:bg-transparent rounded-none shadow-none p-0";
                          inner.style.overflow = "hidden";
                          const close = document.createElement("button");
                          close.className =
                            "fixed top-4 right-4 md:absolute md:top-[16px] md:right-[-3px] z-[70] text-gray-500 hover:text-gray-900 bg-white hover:bg-white/80 rounded";
                          close.innerText = "×";
                          close.style.fontSize = "20px";
                          close.style.padding = "2px 9px";
                          close.style.paddingBottom = "6px";
                          close.onclick = () => {
                            document.body.style.overflow = "";
                            overlay.remove();
                          };
                          const iframe = document.createElement("iframe");
                          iframe.src =
                            "https://meetings-na2.hubspot.com/brian-wilkinson?embed=true";
                          iframe.style.width = "100%";
                          iframe.style.height = "100%";
                          iframe.style.minWidth = "312px";
                          iframe.style.minHeight = "0";
                          iframe.style.border = "none";
                          iframe.style.overflow = "hidden";
                          iframe.style.overscrollBehavior = "contain";
                          iframe.style.display = "block";
                          iframe.setAttribute("scrolling", "no");
                          iframe.setAttribute("data-hs-ignore", "true");
                          // Mobile-only stretch to remove inner gutters without blurring
                          const isMobile =
                            window.matchMedia("(max-width: 767px)").matches;
                          if (isMobile) {
                            iframe.style.position = "absolute";
                            iframe.style.width = "110vw";
                            iframe.style.height = "110vh";
                            iframe.style.left = "-5vw";
                            iframe.style.top = "-5vh";
                          } else {
                            iframe.style.position = "static";
                            iframe.style.width = "100%";
                            iframe.style.height = "100%";
                            iframe.style.left = "0";
                            iframe.style.top = "0";
                          }
                          inner.appendChild(iframe);
                          inner.appendChild(close);
                          overlay.appendChild(inner);
                          document.body.appendChild(overlay);
                          document.body.style.overflow = "hidden";
                        }
                      }}
                    >
                      Schedule Consultation
                    </a>
                  </Button>
                </div>

                {/* Response Time */}
                <div className="pt-4 border-t border-border/50">
                  <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span>Usually responds within 2 hours</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Bottom Bar */}
      <div className="bg-surface border-t border-border/50">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-6 md:space-y-0">
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-8 text-sm text-muted-foreground">
              <div className="font-medium">
                © {currentYear} SkycrestFilms. All rights reserved.
              </div>
              <div className="hidden md:block w-1 h-1 bg-muted-foreground/30 rounded-full"></div>
              <div>Crafted with ❤️ in Rockport</div>
            </div>

            <div className="flex items-center space-x-8 text-sm text-muted-foreground">
              <Link
                to="/privacy"
                className="hover:text-accent transition-colors duration-300 flex items-center space-x-1"
              >
                <Icon name="Shield" size={14} />
                <span>Privacy</span>
              </Link>
              <Link
                to="/terms"
                className="hover:text-accent transition-colors duration-300 flex items-center space-x-1"
              >
                <Icon name="FileText" size={14} />
                <span>Terms</span>
              </Link>
              <Link
                to="/cookies"
                className="hover:text-accent transition-colors duration-300 flex items-center space-x-1"
              >
                <Icon name="Cookie" size={14} />
                <span>Cookies</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
